import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MkButton } from "./MkButton";
import { MkModal } from "./MkModal";
import { MkTable } from "./MkTable";
import type { TableColumn } from "./MkTable";
import type { RelRecord } from "./useRelSearch";

const PAGE_SIZE = 20;

export interface PickerColumn {
    key: string;
    label: string;
    sortable?: boolean;
    align?: "start" | "center" | "end";
}

export type PickerRow = RelRecord & Record<string, unknown>;

export type PickerSource = (query: string, page: number, pageSize: number,
                            order: string) => Promise<{ rows: PickerRow[]; total: number }>;

interface Props {
    open: boolean;
    source: PickerSource;
    title?: string;
    icon?: string;
    columns?: PickerColumn[];
    excludeIds?: number[];
    multi?: boolean;
    canCreate?: boolean;
    pageSize?: number;
    order?: string;
    onSelect(rows: RelRecord[]): void;
    onCreateNew?(query: string): Promise<RelRecord | null>;
    onClose(): void;
}

export function MkRecordPicker({ open, source, title, icon, columns = [], excludeIds = [],
                                 multi = true, canCreate = false, pageSize, order = "",
                                 onSelect, onCreateNew, onClose }: Props) {
    const { t } = useTranslation();
    const size = pageSize || PAGE_SIZE;
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [sortOrder, setSortOrder] = useState(order);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<PickerRow[]>([]);
    const [count, setCount] = useState(0);
    const [selected, setSelected] = useState<Record<number, RelRecord>>({});
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        source(query.trim(), page, size, sortOrder)
            .then((result) => {
                if (cancelled) {
                    return;
                }
                setRecords(result.rows.filter((row) => !excludeIds.includes(row.id)));
                setCount(result.total);
            })
            .catch(() => {
                if (!cancelled) {
                    setRecords([]);
                    setCount(0);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [query, page, size, sortOrder, refresh]);

    const tableColumns: TableColumn<PickerRow>[] = (columns.length ? columns
        : [{ key: "name", label: t("mk.picker.name"), sortable: true }]) as TableColumn<PickerRow>[];
    const selectedRecords = Object.values(selected);
    const selectedCount = selectedRecords.length;

    function confirm() {
        if (!selectedCount) {
            return;
        }
        onSelect(selectedRecords);
        onClose();
    }

    async function createNew() {
        if (!onCreateNew) {
            return;
        }
        const record = await onCreateNew(query.trim());
        if (!record) {
            return;
        }
        if (!multi) {
            onSelect([record]);
            onClose();
            return;
        }
        setSelected((current) => ({ ...current, [record.id]: record }));
        setQuery("");
        setPage(1);
        setRefresh((value) => value + 1);
    }

    return (
        <MkModal open={open} size="lg" title={title || t("mk.picker.title")} icon={icon || "fa-th-list"}
                 onClose={onClose}
                 footer={(
                     <>
                         {multi && (
                             <MkButton variant="success" icon="fa-check"
                                       label={selectedCount ? t("mk.picker.addCount", { value: selectedCount })
                                                            : t("mk.picker.add")}
                                       disabled={!selectedCount} onClick={confirm} />
                         )}
                         {canCreate && onCreateNew && (
                             <MkButton variant="primary" icon="fa-plus" label={t("mk.picker.new")}
                                       onClick={() => void createNew()} />
                         )}
                         <MkButton variant="ghost" icon="fa-times" label={t("mk.cancel")} onClick={onClose} />
                     </>
                 )}>
            <div className="mk-picker">
                <MkTable columns={tableColumns} rows={records} rowKey={(row) => row.id}
                         loading={loading} searchable searchPlaceholder={t("mk.table.search")}
                         emptyText={t("mk.table.noRecords")} emptyIcon="fa-inbox"
                         pageSize={size} total={count} page={page} selectable={multi} hover
                         onSearch={(value) => {
                             setQuery(value);
                             setPage(1);
                         }}
                         onSort={(next) => {
                             setSortOrder(next);
                             setPage(1);
                         }}
                         onPage={(next) => setPage(next)}
                         onSelectionChange={(rows) => setSelected((current) => {
                             const pageIds = new Set(records.map((record) => record.id));
                             const next: Record<number, RelRecord> = {};
                             for (const [id, record] of Object.entries(current)) {
                                 if (!pageIds.has(Number(id))) {
                                     next[Number(id)] = record;
                                 }
                             }
                             for (const row of rows) {
                                 next[row.id] = { id: row.id, name: row.name };
                             }
                             return next;
                         })}
                         onRowClick={(row) => {
                             if (multi) {
                                 return;
                             }
                             onSelect([{ id: row.id, name: row.name }]);
                             onClose();
                         }} />
                <div className="mk-picker-hint">
                    {multi ? t("mk.picker.hintMulti") : t("mk.picker.hintSingle")}
                </div>
            </div>
        </MkModal>
    );
}
